// src/utils/auth.js
import API from "../api"; // used by syncRolesFromServer
import sessionManager from "./sessionManager"; // Import session manager

// --- JWT helpers ------------------------------------------------------------
function base64UrlDecode(str) {
  try {
    const pad = "=".repeat((4 - (str.length % 4)) % 4);
    const b64 = (str + pad).replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(b64);
    return JSON.parse(json);
  } catch {
    return {};
  }
}

export function parseJwt(token) {
  if (!token) return {};
  const parts = token.split(".");
  if (parts.length !== 3) return {};
  return base64UrlDecode(parts[1]);
}

// --- token storage ----------------------------------------------------------
export function setAuth(token) {
  localStorage.setItem("token", token);
  // Initialize session tracking after login
  sessionManager.init();
}
export function getToken() {
  return localStorage.getItem("token") || "";
}
export function clearAuth() {
  // Clear everything related to auth/roles
  localStorage.removeItem("token");
  localStorage.removeItem("role"); // legacy
  localStorage.removeItem("activeRole"); // selected role
  localStorage.removeItem("roles"); // cached roles from /me
  sessionStorage.removeItem("_setupComplete");
  localStorage.removeItem("_setupComplete");
  // Destroy session tracking on logout
  sessionManager.destroy();
}

// Hard logout: clear, broadcast, and force a full navigation to /login
export function logoutAndRedirect() {
  try {
    clearAuth();
    // Notify other tabs to clear too
    localStorage.setItem("force-logout", String(Date.now()));
  } finally {
    // Kill any in-memory state (including React tree/axios interceptors in RAM)
    window.location.replace("/login");
  }
}

// --- identity ---------------------------------------------------------------
export function getUsername() {
  const payload = parseJwt(getToken());
  return payload.sub || payload.username || "";
}

/** Numeric user id from JWT (claim set by JwtService). */
export function getUserId() {
  const payload = parseJwt(getToken());
  const raw = payload.userId ?? payload.user_id ?? payload.uid;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// --- roles ------------------------------------------------------------------
const ALLOWED = new Set(["ADMIN", "MANAGER", "COACH", "STUDENT", "PARENT"]);

function normalizeRolesList(list) {
  if (!Array.isArray(list)) return [];
  return Array.from(
    new Set(
      list
        .filter(Boolean)
        .map((r) => String(r).toUpperCase())
        .filter((r) => ALLOWED.has(r)),
    ),
  );
}

// Prefer cached roles from /me; fallback to token claims
export function getRoles() {
  try {
    const cached = JSON.parse(localStorage.getItem("roles") || "[]");
    const norm = normalizeRolesList(cached);
    if (norm.length) return norm;
  } catch {
    /* ignore */
  }

  const payload = parseJwt(getToken());
  let roles = [];

  if (Array.isArray(payload.roles)) {
    roles = payload.roles;
  } else if (typeof payload.roles === "string") {
    roles = payload.roles.split(/[,\s]+/);
  } else if (Array.isArray(payload.authorities)) {
    roles = payload.authorities.map((a) => String(a).replace(/^ROLE_/, ""));
  } else if (typeof payload.role === "string") {
    roles = [payload.role];
  }

  return normalizeRolesList(roles);
}

export function normalizeRole(role) {
  return role ? String(role).toUpperCase() : "";
}

export function setActiveRole(role) {
  if (!role) return;
  localStorage.setItem("activeRole", normalizeRole(role));
}

export function getActiveRole() {
  const stored = localStorage.getItem("activeRole");
  if (stored) return normalizeRole(stored);

  const roles = getRoles();
  if (roles.length === 1) return roles[0];
  return "";
}

export function shouldPromptRole() {
  const roles = getRoles();
  const stored = localStorage.getItem("activeRole");
  return roles.length > 1 && !stored;
}

export function getNormalizedRole(preferred) {
  if (preferred) {
    const cleaned = String(preferred).toUpperCase().replace(/^ROLE_/, '').trim();
    if (ALLOWED.has(cleaned)) return cleaned;
  }
  const active = getActiveRole();
  if (active) return active;
  const roles = getRoles();
  if (roles.length === 1) return roles[0];
  return "";
}

// --- auth status ------------------------------------------------------------
export function isAuthenticated() {
  const token = getToken();
  if (!token) return false;
  const payload = parseJwt(token);
  const expMs = payload.exp ? Number(payload.exp) * 1000 : 0;
  return expMs > Date.now();
}

// --- routing helpers ---------------------------------------------------------
export function targetPathForRole(role) {
  switch (normalizeRole(role)) {
    case "ADMIN":
      return "/admin";
    case "MANAGER":
      return "/manager";
    case "COACH":
      return "/coach";
    case "STUDENT":
      return "/student";
    case "PARENT":
      return "/parent";
    default:
      return "/login";
  }
}

// --- sync roles with backend (authoritative) --------------------------------
export async function syncRolesFromServer() {
  try {
    const res = await API.get("/me");
    const roles = normalizeRolesList(res?.data?.roles || []);
    if (roles.length) {
      localStorage.setItem("roles", JSON.stringify(roles));
    } else {
      localStorage.removeItem("roles");
    }
    return roles;
  } catch {
    return getRoles();
  }
}
